import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ContainerQuery } from 'react-container-query';
import classNames from 'classnames';
import classes from './OfferOptions.scss';
import { breakpoints } from "cityads-v3/utils/styleUtils";

import OfferOption from './OfferOption';

import DateFormat from 'cityads-v3/components/Format/DateFormat';
import NumberFormat, { FORMAT_NUMBER, FORMAT_PERCENT } from 'cityads-v3/components/Format/NumberFormat';
import PaidFormat from 'cityads-v3/components/Format/PaidFormat';
import Typography from '@mui/material/Typography'

const AVG_OPTION_WIDTH = 90;

export default function OfferOptions({ title, options, offerStat, offerInfo, className, align }) {
  const isMobile = useSelector(state => state.app.isMobile);
  const currencies = useSelector(state => state.currency.currencyById);
  const offerOptions = useMemo(() => (
    options.map(option => {
      let value = '';
      let suffix = option.suffix || '';

      switch (option.format) {
        default:
          if (option.formatter) {
            value = option.formatter(offerInfo[option.name], offerInfo, offerStat);
            break;
          }
          value = offerInfo[option.name];
          break;

        case 'notBlank':
          value = offerInfo[option.name];
          if (!value) {
            return false;
          }
          break;

        case 'date':
          value = <DateFormat date={offerInfo[option.name]} />;
          break;

        case 'period':
          value = (<Fragment>
            <DateFormat date={offerInfo[option.start]} />—<DateFormat date={offerInfo[option.end]} />
          </Fragment>)
          break;

        case 'currency':
          suffix = currencies[offerInfo.currency_id]?.name;
          // falls through
        case 'number':
        case 'percent':
          const number = typeof offerInfo[option.name] === 'undefined' ? offerStat && offerStat[option.name] : offerInfo[option.name];
          value = <NumberFormat
            number={+number}
            format={option.format === 'percent' ? FORMAT_PERCENT : FORMAT_NUMBER}
            formatter={option.formatter}
            precision={option.precision}
          />;
          break;

        case 'paid':
          const param = offerInfo[option.name];
          value = param ? <PaidFormat commissionData={param} precision={option.precision} /> : null;
          break;
      }

      return {
        option,
        value,
        suffix,
      }
    })
      .filter(({ option }) => !!option)
  ), [currencies, offerInfo, offerStat, options]);

  const minWidth = AVG_OPTION_WIDTH * offerOptions.length;
  const responsiveQuery = {
    dense: {
      maxWidth: isMobile ? breakpoints.sm : minWidth
    }
  }

  return (
    <ContainerQuery query={responsiveQuery}>
      {params => (
        <div className={className}>
          {title ? (
            <Typography
              component="div"
              className={classes.title}
            >
              {title}
            </Typography>
          ) : false}

          <div
            className={classNames(classes.items, {
              [classes['items--alignleft']]: align === 'left',
              [classes.items__dense]: params.dense,
            })}
          >
            {offerOptions.map(({ option, value, suffix }) => (
              <OfferOption
                dense={params.dense}
                key={option.name}
                name={option.name}
                title={option.title}
                value={value}
                suffix={suffix}
                tooltip={option.tooltip}
              />
            ))}
          </div>
        </div>
      )}
    </ContainerQuery>
  );
}

OfferOptions.propTypes = {
  className:  PropTypes.string,
  currencies: PropTypes.object,
  options:    PropTypes.array,
  offerStat:  PropTypes.object,
  offerInfo:  PropTypes.object,
  title:      PropTypes.string,
  align:      PropTypes.oneOf(['left', 'space-between'])
}

OfferOptions.defaultProps = {
  options: [],
  align: 'space-between'
}
